import React, { MouseEvent, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { Updater } from "use-immer";
import { v4 } from "uuid";

import { IgButton, IgTooltip, IgTypography } from "@intelligems/ig-theme";
import {
  ExperienceDto,
  GetScriptStatusResponseBodyType,
  OrganizationDto,
} from "@intelligems/ig-types";
import { MoreHoriz } from "@mui/icons-material";
import { Menu, MenuItem } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";

import { useConfiguration } from "../../../../../api/configurations/use-configuration";
import { useDuplicateExperience } from "../../../../../api/experiences/duplicate-experience";
import { useGetExperiencesList } from "../../../../../api/experiences-list/get-experiences-list";
import { useGetHistory } from "../../../../../api/histories/get-history";
import { processesKeys } from "../../../../../api/queryKeys";
import { useIsAdmin } from "../../../../../api/user-auths/is-admin";
import { checkSubscriptionStatus } from "../../../../../components/ScopeWrappers/helpers";
import {
  DASHBOARD_ROUTES,
  EXPERIMENT_ROUTES,
} from "../../../../../routes/routes";
import { setDialog } from "../../../../../store/slices/global";
import {
  mpClickAnalyticsButton,
  mpClickButton,
  mpClickDuplicateExperienceButton,
  mpClickEditExperienceButton,
} from "../../../../../utils/mixpanel";
import { buildEditUrlPath } from "../../../utils/utils";

import { getConfirmedIntegrationHistoryAction } from "./experiencesList/utils";

export interface MoreMenuProps {
  scriptStatus: GetScriptStatusResponseBodyType | null;
  organization: OrganizationDto;
  experience: ExperienceDto;
  setCurrExperience: Updater<ExperienceDto | undefined>;
}

export const MoreMenu = ({
  organization,
  experience,
  scriptStatus,
  setCurrExperience,
}: MoreMenuProps) => {
  const dispatch = useDispatch();
  const isAdmin = useIsAdmin();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const downloadPriceCSVEnabled = useConfiguration("enable_price_csv_download");
  const shouldDuplicateProducts = useConfiguration<boolean>(
    "should_duplicate_products"
  )!.value;

  const enableScheduleTests = useConfiguration<boolean>(
    "enable_schedule_tests"
  )!.value;

  const history = useGetHistory({
    body: undefined,
    pathParams: { experienceId: experience.id },
    queryParams: undefined,
  });

  const duplicateExperiment = useDuplicateExperience("experiment");
  const queryClient = useQueryClient();

  const canAnalyzeExperiment = checkSubscriptionStatus() || isAdmin;
  const experiencesList = useGetExperiencesList({ category: "experiment" });

  const navigate = useNavigate();
  const open = Boolean(anchorEl);
  const handleClick = (event: MouseEvent<HTMLElement>) => {
    mpClickButton("experiment-page-more-button");
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const canCreateShippingTest = useMemo(() => {
    if (experiencesList.isSuccess) {
      return (
        experiencesList.data.experiencesList.filter(
          (exp) =>
            exp.testTypes.hasTestShipping &&
            (exp.status === "pending" || exp.status === "started")
        ).length === 0
      );
    } else return true;
  }, [experiencesList]);

  const editPath = useMemo(() => {
    return buildEditUrlPath(experience);
  }, [experience]);

  return (
    <>
      <IgTooltip title={"View More Options"}>
        <IgButton
          color={"black"}
          variant={"text"}
          id="experiment-page-more-button"
          aria-controls="more-positioned-menu"
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          onClick={handleClick}
        >
          <MoreHoriz color={"primary"} />
        </IgButton>
      </IgTooltip>
      <Menu
        id="more-positioned-menu"
        aria-labelledby="more-positioned-button"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <Link
          style={{ textDecoration: "none", color: "black" }}
          to={editPath}
          state={{ id: experience.id }}
          onClick={() => {
            mpClickEditExperienceButton();
          }}
        >
          <MenuItem>
            <IgTypography>
              {experience.status === "ended" || experience.isExternal
                ? "View"
                : "Edit"}
            </IgTypography>
          </MenuItem>
        </Link>
        {experience.status !== "ended" ? (
          <IgTooltip
            title={!enableScheduleTests ? "Upgrade plan for access" : ""}
          >
            <MenuItem
              disabled={!enableScheduleTests && !history.isSuccess}
              onClick={() => {
                setCurrExperience(experience);
                if (
                  !getConfirmedIntegrationHistoryAction(
                    history.data?.history || []
                  )
                ) {
                  dispatch(
                    setDialog({
                      dialog: "confirmIgChecklist",
                      open: true,
                      props: { isAdminOverride: false, isSchedule: true },
                    })
                  );
                } else {
                  dispatch(
                    setDialog({
                      dialog: "scheduleTest",
                      open: true,
                      props: {
                        experience,
                      },
                    })
                  );
                }
              }}
            >
              <IgTypography>Schedule Test</IgTypography>
            </MenuItem>
          </IgTooltip>
        ) : null}
        {experience.startedAtTs &&
          experience.startedAtTs > 0 &&
          canAnalyzeExperiment && (
            <Link
              style={{ textDecoration: "none", color: "black" }}
              onClick={() => {
                mpClickAnalyticsButton(
                  experience.name,
                  experience.id!,
                  "MoreMenu analytics button"
                );
              }}
              to={`${
                DASHBOARD_ROUTES.Dashboard_Overview
              }?experienceId=${experience.id!}`}
            >
              <MenuItem>
                <IgTypography>Analyze</IgTypography>
              </MenuItem>
            </Link>
          )}
        {!experience.isExternal &&
          (!experience.testTypes.hasTestShipping || canCreateShippingTest) && (
            <MenuItem
              style={{ textDecoration: "none", color: "black" }}
              onClick={() => {
                mpClickDuplicateExperienceButton();
                queryClient.setQueryData(processesKeys.one(organization.id), {
                  processes: [
                    {
                      id: v4(),
                      orgId: organization.id,
                      type: "duplicateExperience",
                      severity: "info",
                      title: `Duplicating ${experience.name}`,
                      isLoading: true,
                    },
                  ],
                });
                duplicateExperiment.mutate({
                  body: undefined,
                  pathParams: { experienceId: experience.id! },
                  queryParams: undefined,
                });
                handleClose();
              }}
              disabled={duplicateExperiment.isPending}
            >
              <IgTypography>Duplicate</IgTypography>
            </MenuItem>
          )}
        {experience.isExternal ? null : (
          <MenuItem
            onClick={() => {
              setCurrExperience(experience);
              if (!scriptStatus?.exists) {
                dispatch(
                  setDialog({
                    dialog: "redirectToPluginDialog",
                    open: true,
                    props: {
                      id: experience.id,
                      experience,
                      path:
                        experience.previewPath ||
                        experience.devicePreview?.path,
                    },
                  })
                );
              } else {
                dispatch(
                  setDialog({
                    dialog: "previewExperiment",
                    open: true,
                    props: {
                      id: experience.id,
                      path:
                        experience.previewPath ||
                        experience.devicePreview?.path,
                    },
                  })
                );
              }
            }}
          >
            <IgTypography>Preview</IgTypography>
          </MenuItem>
        )}
        {isAdmin && (
          <MenuItem
            onClick={() => {
              navigate(`${EXPERIMENT_ROUTES.Compare}/${experience.id}`);
            }}
          >
            <IgTypography>Compare Groups</IgTypography>
          </MenuItem>
        )}
        {shouldDuplicateProducts && experience.testTypes.hasTestPricing ? (
          <MenuItem
            onClick={() => {
              navigate(
                `${EXPERIMENT_ROUTES.DuplicateProducts}/${experience.id}`
              );
              handleClose();
            }}
          >
            <IgTypography>View Duplicates</IgTypography>
          </MenuItem>
        ) : null}
        {!experience.isExternal && experience.status !== "started" && (
          <MenuItem
            onClick={() => {
              setCurrExperience(experience);
              dispatch(
                setDialog({
                  dialog: "confirmExperimentDeletion",
                  open: true,
                  props: { experiment: experience },
                })
              );
              handleClose();
            }}
          >
            <IgTypography>Delete</IgTypography>
          </MenuItem>
        )}
        {isAdmin &&
          (experience.status === "started" || experience.status === "ended") &&
          experience.testTypes.hasTestShipping && (
            <Link
              style={{ textDecoration: "none", color: "black" }}
              to={`/shipping-profiles/${experience.id!}`}
              state={{ id: experience.id }}
            >
              <MenuItem>
                <IgTypography>View Shipping Profile Backups</IgTypography>
              </MenuItem>
            </Link>
          )}
        {(downloadPriceCSVEnabled?.value || isAdmin) &&
          experience.testTypes.hasTestPricing && (
            <MenuItem
              onClick={() => {
                setCurrExperience(experience);
                dispatch(
                  setDialog({
                    dialog: "downloadPriceCsv",
                    open: true,
                  })
                );
                handleClose();
              }}
            >
              <IgTypography>Get Price CSV</IgTypography>
            </MenuItem>
          )}
        <MenuItem
          onClick={() => {
            setCurrExperience(experience);
            dispatch(setDialog({ open: true, dialog: "experimentInfo" }));
            handleClose();
          }}
        >
          <IgTypography>Show Info</IgTypography>
        </MenuItem>
      </Menu>
    </>
  );
};
